import { useMemo } from 'react';

/**
 * Centralized hook to transform bars from barsByKey into chart-ready data
 * Handles deduplication and formatting in one place with careful memoization
 */
export const useChartData = (bars) => {
  return useMemo(() => {
    if (!bars || !Array.isArray(bars) || bars.length === 0) {
      return [];
    }

    // Helper to safely parse float values
    const safeParseFloat = (value) => {
      if (value === undefined || value === null || value === '' || value === 'null') {
        return 0;
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    const resolveTimestampMs = (bar) => {
      if (bar?.Epoch != null) {
        const epochMs = Number(bar.Epoch);
        if (Number.isFinite(epochMs) && epochMs > 0) return epochMs;
      }
      const timestamp = bar?.TimeStamp || bar?.TradeTime;
      if (!timestamp) return NaN;
      const parsed = new Date(timestamp).getTime();
      return Number.isFinite(parsed) ? parsed : NaN;
    };

    // Transform and deduplicate bars by chart epoch/time
    const timeMap = new Map();
    
    for (const bar of bars) {
      const timestampMs = resolveTimestampMs(bar);
      const timeValue = Math.floor(timestampMs / 1000);
      
      if (isNaN(timeValue) || timeValue <= 0) continue;

      const formatted = {
        time: timeValue,
        open: safeParseFloat(bar.Open),
        high: safeParseFloat(bar.High),
        low: safeParseFloat(bar.Low),
        close: safeParseFloat(bar.Close),
        volume: safeParseFloat(bar.TotalVolume || bar.Volume),
      };

      // Validate all required fields are valid numbers
      if (
        isNaN(formatted.time) ||
        isNaN(formatted.open) ||
        isNaN(formatted.high) ||
        isNaN(formatted.low) ||
        isNaN(formatted.close)
      ) {
        continue;
      }

      // Deduplicate by bar time - keep the latest stamp when collisions exist.
      const existing = timeMap.get(timeValue);
      const existingTimestampMs = existing?._timestampMs || 0;
      
      if (!existing || timestampMs >= existingTimestampMs) {
        formatted._timestampMs = timestampMs; // Store for comparison
        timeMap.set(timeValue, formatted);
      }
    }

    // Convert to sorted array
    const chartData = Array.from(timeMap.values());
    chartData.sort((a, b) => a.time - b.time);

    return chartData;
  }, [bars]);
};
